import React from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import type {
  RewardHistoryItem,
  RewardHistoryMeta,
} from "@/api/models/api-types";

interface RewardsHistorySectionProps {
  items: RewardHistoryItem[];
  meta?: RewardHistoryMeta;
  isLoading: boolean;
  isError: boolean;
  page: number;
  onPageChange: (page: number) => void;
}

const formatDate = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const RewardsHistorySection: React.FC<RewardsHistorySectionProps> = ({
  items,
  meta,
  isLoading,
  isError,
  page,
  onPageChange,
}) => {
  const { t } = useTranslation();
  const totalPage = meta?.totalPage ?? 1;

  return (
    <div className="flex flex-col gap-3">
      <h2 className="text-lg font-semibold text-text-primary dark:text-gray-100">
        {t("settings.rewards.history")}
      </h2>

      {isLoading ? (
        <div className="h-40 bg-gray-50 dark:bg-gray-700 rounded-xl animate-pulse" />
      ) : isError ? (
        <div className="rounded-xl border border-border-secondary dark:border-gray-600 bg-bg-primary dark:bg-gray-800 p-6 text-center text-sm text-text-tertiary dark:text-gray-400">
          {t("settings.rewards.historyError")}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-border-secondary dark:border-gray-600 bg-bg-primary dark:bg-gray-800 p-6 text-center text-sm text-text-tertiary dark:text-gray-400">
          {t("settings.rewards.historyEmpty")}
        </div>
      ) : (
        <>
          <div className="overflow-hidden rounded-xl border border-border-secondary dark:border-gray-600 bg-bg-primary dark:bg-gray-800">
            {items.map((item, index) => (
              <div
                key={item._id ?? index}
                className={cn(
                  "flex items-center justify-between gap-4 px-4 py-3",
                  index !== 0 &&
                    "border-t border-border-secondary dark:border-gray-700"
                )}
              >
                <div className="flex flex-col gap-0.5 min-w-0">
                  <p className="text-sm font-medium text-text-primary dark:text-gray-100 truncate">
                    {item.note ||
                      item.source ||
                      t("settings.rewards.historyDefaultLabel")}
                  </p>
                  <p className="text-xs text-text-quaternary dark:text-gray-400">
                    {formatDate(item.createdAt)}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-0.5 shrink-0">
                  <p
                    className={cn(
                      "text-sm font-semibold",
                      item.amount < 0
                        ? "text-text-secondary dark:text-gray-300"
                        : "text-emerald-600 dark:text-emerald-400"
                    )}
                  >
                    {item.amount > 0 ? "+" : ""}
                    {item.amount.toLocaleString()}
                  </p>
                  <p className="text-xs text-text-quaternary dark:text-gray-400">
                    {t("settings.rewards.balanceLabel", {
                      balance: item.balance.toLocaleString(),
                    })}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPage > 1 && (
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => onPageChange(page - 1)}
                disabled={page <= 1}
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-border-secondary dark:border-gray-600 text-text-secondary dark:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-bg-secondary dark:hover:bg-gray-700 transition-colors"
                aria-label={t("common.previous")}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-sm text-text-tertiary dark:text-gray-400">
                {t("settings.rewards.pageOf", { page, total: totalPage })}
              </span>
              <button
                type="button"
                onClick={() => onPageChange(page + 1)}
                disabled={page >= totalPage}
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-border-secondary dark:border-gray-600 text-text-secondary dark:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-bg-secondary dark:hover:bg-gray-700 transition-colors"
                aria-label={t("common.next")}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
